import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useDefaultSender } from '@/hooks/useDefaultSender';
import { toast } from 'sonner';
import { Send, Loader2, CheckCircle, Mail, CalendarIcon, Clock } from 'lucide-react';
import { Campaign } from '@/services/campaignService';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
}

const CampaignTestSendDialog: React.FC<Props> = ({ open, onOpenChange, campaign }) => {
  const { user, profile } = useAuth();
  const { defaultSender, isLoading: senderLoading } = useDefaultSender();
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ from_email: string; to_email: string; subject: string; scheduled?: boolean; scheduled_for?: string } | null>(null);
  const [senderSource, setSenderSource] = useState<string>(campaign.sender_config?.source || 'user_sendgrid');
  const [companyEmail, setCompanyEmail] = useState<string | null>(null);
  const [sendMode, setSendMode] = useState<'now' | 'schedule'>('now');
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>(undefined);
  const [scheduleTime, setScheduleTime] = useState('09:00');

  // Fetch company default email for display
  useEffect(() => {
    if (!profile?.company_id) return;
    const fetchCompanyEmail = async () => {
      try {
        const { data } = await supabase
          .from('companies')
          .select('default_from_email')
          .eq('id', profile.company_id)
          .single();
        setCompanyEmail((data as any)?.default_from_email || null);
      } catch {
        setCompanyEmail(null);
      }
    };
    fetchCompanyEmail();
  }, [profile?.company_id]);

  const userEmailLabel = defaultSender?.from_email
    ? `My Verified Email (${defaultSender.from_email})`
    : 'My Verified Email';

  const companyEmailLabel = companyEmail
    ? `Company Default (${companyEmail})`
    : 'Company Default';

  const handleSend = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    if (sendMode === 'schedule' && !scheduleDate) {
      toast.error('Please select a date for scheduling');
      return;
    }

    setSending(true);
    setResult(null);

    try {
      let scheduled_for: string | undefined;
      if (sendMode === 'schedule' && scheduleDate) {
        const [hours, minutes] = scheduleTime.split(':').map(Number);
        const dt = new Date(scheduleDate);
        dt.setHours(hours, minutes, 0, 0);
        scheduled_for = dt.toISOString();
      }

      const { data, error } = await supabase.functions.invoke('test-send-campaign', {
        body: { campaign_id: campaign.id, test_email: testEmail, sender_source: senderSource, scheduled_for },
      });
      if (error) throw new Error(error.message);
      if (!data?.success) throw new Error(data?.error || 'Test send failed');

      setResult({
        from_email: data.from_email,
        to_email: data.to_email,
        subject: data.subject,
        scheduled: data.scheduled,
        scheduled_for: data.scheduled_for,
      });
      toast.success(data.scheduled ? 'Test email scheduled!' : 'Test email sent!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  const isScheduleMode = sendMode === 'schedule';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setResult(null); onOpenChange(v); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Test Send
          </DialogTitle>
          <DialogDescription>
            Send a test email to yourself using the first queued email from this campaign. Subject will be prefixed with [TEST].
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Campaign info */}
          <div className="p-3 rounded-lg bg-muted/50 border border-border/60 space-y-1 text-sm">
            <div><span className="text-muted-foreground">Campaign:</span> {campaign.name}</div>
          </div>

          {/* Sender source selector */}
          <div className="space-y-1.5">
            <Label htmlFor="sender-source">Send from</Label>
            <Select value={senderSource} onValueChange={setSenderSource}>
              <SelectTrigger id="sender-source">
                <SelectValue placeholder="Select sender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user_sendgrid">{userEmailLabel}</SelectItem>
                <SelectItem value="system_sendgrid">{companyEmailLabel}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Test email input */}
          <div className="space-y-1.5">
            <Label htmlFor="test-email">Send test to</Label>
            <Input
              id="test-email"
              type="email"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="your@email.com"
            />
          </div>

          {/* Send mode toggle */}
          <div className="space-y-2">
            <Label>When to send</Label>
            <RadioGroup value={sendMode} onValueChange={(v) => setSendMode(v as 'now' | 'schedule')} className="flex gap-4">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="now" id="send-now" />
                <Label htmlFor="send-now" className="cursor-pointer font-normal">Send Now</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="schedule" id="send-schedule" />
                <Label htmlFor="send-schedule" className="cursor-pointer font-normal">Schedule for Later</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Schedule date/time picker */}
          {isScheduleMode && (
            <div className="space-y-3 p-3 rounded-lg border border-border/60 bg-muted/30">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !scheduleDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {scheduleDate ? format(scheduleDate, 'PPP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={scheduleDate}
                      onSelect={setScheduleDate}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="schedule-time">Time</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="schedule-time"
                    type="time"
                    value={scheduleTime}
                    onChange={e => setScheduleTime(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Uses your local timezone</p>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/20 space-y-1 text-sm">
              <div className="flex items-center gap-1.5 text-green-600 font-medium">
                <CheckCircle className="h-4 w-4" />
                {result.scheduled ? 'Test email scheduled!' : 'Test email sent!'}
              </div>
              <div><span className="text-muted-foreground">From:</span> {result.from_email}</div>
              <div><span className="text-muted-foreground">To:</span> {result.to_email}</div>
              <div><span className="text-muted-foreground">Subject:</span> {result.subject}</div>
              {result.scheduled_for && (
                <div><span className="text-muted-foreground">Scheduled for:</span> {new Date(result.scheduled_for).toLocaleString()}</div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button
            onClick={handleSend}
            disabled={sending || !testEmail || (isScheduleMode && !scheduleDate)}
            className="gap-1.5"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : isScheduleMode ? <CalendarIcon className="h-4 w-4" /> : <Send className="h-4 w-4" />}
            {sending ? 'Processing…' : isScheduleMode ? 'Schedule Test' : 'Send Test'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignTestSendDialog;
